import React, { useState, useEffect } from "react";
import { Button, Dropdown, Header, Message, Segment, Input, Checkbox, Popup } from "semantic-ui-react";
import { useSelector, useDispatch } from "react-redux";
// No need to import reference config functions anymore since we use isReferenceDataset flag
import { getDataSet } from "../../apis";
import { editDataset } from "../../actions/dataConsoleAction";
import { updateOrganization, fetchOrganizationDetails } from "../../actions/adminConsoleAction";

const ReferenceDatasetSettings = ({ showToaster }) => {
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [currentReferenceDataset, setCurrentReferenceDataset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickChatLabel, setQuickChatLabel] = useState("");
  const [labelLoading, setLabelLoading] = useState(false);
  const [quickChatDisabled, setQuickChatDisabled] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  const dispatch = useDispatch();
  const orgDetails = useSelector((state) => state.admin.details);
  const userToken = useSelector((state) => state.auth.token);

  useEffect(() => {
    loadDatasets();
  }, []);

  useEffect(() => {
    // Load current reference after datasets are loaded
    if (datasets.length > 0) {
      loadCurrentReference();
    }
  }, [datasets]);

  useEffect(() => {
    // Initialize QuickChat label and disabled state when org details are loaded
    if (orgDetails?.orgName) {
      const defaultLabel = `${orgDetails.orgName} Assistant`;
      setQuickChatLabel(orgDetails.quickChatLabel || defaultLabel);
      setQuickChatDisabled(orgDetails.quickChatDisabled || false);
    }
  }, [orgDetails]);

  const loadCurrentReference = () => {
    // Find the current reference dataset by looking for isReferenceDataset flag
    const referenceDataset = datasets.find(dataset => dataset.isReferenceDataset);
    if (referenceDataset) {
      setCurrentReferenceDataset({
        name: `Reference_${referenceDataset.datasetName}`,
        sourceDatasetId: referenceDataset.id,
        createdAt: referenceDataset.createdUTC || referenceDataset.createdAt
      });
      setSelectedDatasetId(referenceDataset.id);
    } else {
      setCurrentReferenceDataset(null);
      setSelectedDatasetId("");
    }
  };

  const loadDatasets = async () => {
    try {
      const response = await getDataSet();
      if (response?.data) {
        // Filter out QuickChat datasets from the list
        const filteredDatasets = response.data.filter(dataset => 
          !dataset.isQuickChat && !dataset.datasetName.includes('quickChat')
        );
        setDatasets(filteredDatasets);
      }
    } catch (error) {
      console.error("Error loading datasets:", error);
      showToaster("Failed to load datasets", "error");
    }
  };

  const updateDatasetReferenceFlag = async (datasetId, isReference, datasetName) => {
    try {
      const dataset = datasets.find(d => d.id === datasetId);
      if (!dataset) {
        throw new Error('Dataset not found');
      }

      const updatePayload = {
        datasetName: dataset.datasetName,
        description: dataset.description || '',
        isReferenceDataset: isReference,
        checkdatasetName: false
      };

      console.log(`Updating dataset ${datasetName} isReferenceDataset flag to:`, isReference);
      await dispatch(editDataset(updatePayload, datasetId));
    } catch (error) {
      console.error(`Error updating dataset ${datasetName} reference flag:`, error);
      throw error;
    }
  };

  const handleSetReference = async () => {
    if (!selectedDatasetId) {
      showToaster("Please select a dataset", "error");
      return;
    }

    setLoading(true);
    try {
      // Find the selected dataset name
      const selectedDataset = datasets.find(d => d.id === selectedDatasetId);
      const configName = `Reference_${selectedDataset.datasetName}`;
      
      // Step 1: If there's an existing reference dataset, set its flag to false
      if (currentReferenceDataset && currentReferenceDataset.sourceDatasetId !== selectedDatasetId) {
        const oldDataset = datasets.find(d => d.id === currentReferenceDataset.sourceDatasetId);
        if (oldDataset) {
          console.log('Clearing old reference dataset flag...');
          await updateDatasetReferenceFlag(currentReferenceDataset.sourceDatasetId, false, oldDataset.datasetName);
        }
      }
      
      // Step 2: Set the new dataset's flag to true
      console.log('Setting new reference dataset flag...');
      await updateDatasetReferenceFlag(selectedDatasetId, true, selectedDataset.datasetName);
      
      // Step 3: Update current reference state
      setCurrentReferenceDataset({
        name: configName,
        sourceDatasetId: selectedDatasetId,
        createdAt: new Date().toISOString()
      });
      
      // Step 4: Reload datasets to reflect the updated flags
      await loadDatasets();
      
      showToaster(`Reference dataset set to "${selectedDataset.datasetName}" successfully`, "success");
    } catch (error) {
      console.error("Error setting reference dataset:", error);
      showToaster("Failed to set reference dataset", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClearReference = async () => {
    if (!currentReferenceDataset) return;
    
    setLoading(true);
    try {
      // Set the current reference dataset's flag to false
      const currentDataset = datasets.find(d => d.id === currentReferenceDataset.sourceDatasetId);
      if (currentDataset) {
        console.log('Clearing reference dataset flag...');
        await updateDatasetReferenceFlag(currentReferenceDataset.sourceDatasetId, false, currentDataset.datasetName);
      }
      
      // Clear state
      setCurrentReferenceDataset(null);
      setSelectedDatasetId("");
      
      // Reload datasets to reflect the updated flags
      await loadDatasets();
      
      showToaster("Reference dataset cleared successfully", "success");
    } catch (error) {
      console.error("Error clearing reference dataset:", error);
      showToaster("Failed to clear reference dataset", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuickChatLabel = async () => {
    if (!quickChatLabel.trim()) {
      showToaster("Label cannot be empty", "error");
      return;
    }

    setLabelLoading(true);
    try {
      // Get organization ID from orgDetails._id
      const organizationId = orgDetails?._id;
      // Update organization with the new quickChatLabel
      const organizationData = {
        ...orgDetails,
        orgId: organizationId, // Add organization ID to the request
        quickChatLabel: quickChatLabel.trim()
      };
      await dispatch(updateOrganization(organizationData));
      // Refresh org details to get updated data
      await dispatch(fetchOrganizationDetails(true));
      showToaster("QuickChat Assistant label updated successfully", "success");
    } catch (error) {
      console.error("Error saving QuickChat label:", error);
      showToaster("Failed to save label", "error");
    } finally {
      setLabelLoading(false);
    }
  };

  const handleResetQuickChatLabel = () => {
    const defaultLabel = orgDetails?.orgName ? `${orgDetails.orgName} Assistant` : "Assistant";
    setQuickChatLabel(defaultLabel);
  };

  const handleQuickChatToggle = async (event, { checked }) => {
    // Prevent multiple clicks while API is processing
    if (toggleLoading) {
      return;
    }

    // Optimistic UI update - update toggle immediately for responsive UI
    const previousState = quickChatDisabled;
    setQuickChatDisabled(checked);
    setToggleLoading(true);
    
    try {
      // Get organization ID from orgDetails._id
      const organizationId = orgDetails?._id;
      // Update organization with the new quickChatDisabled flag
      const organizationData = {
        ...orgDetails,
        orgId: organizationId, // Add organization ID to the request
        quickChatDisabled: checked
      };
      
      // Only make PUT call - no need to wait for GET call
      await dispatch(updateOrganization(organizationData));
      
      // Show success message immediately after PUT completes
      showToaster(
        checked ? "QuickChat enabled for users with QuickChat role" : "QuickChat disabled for all users", 
        "success"
      );
      
      // Update org details in background (no await)
      dispatch(fetchOrganizationDetails(true));
      
    } catch (error) {
      console.error("Error updating QuickChat visibility:", error);
      // Revert optimistic update on error
      setQuickChatDisabled(previousState);
      showToaster("Failed to update QuickChat visibility", "error");
    } finally {
      setToggleLoading(false);
    }
  };

  const datasetOptions = datasets.map(dataset => ({
    key: dataset.id,
    value: dataset.id,
    text: dataset.datasetName,
    description: `Created: ${new Date(dataset.createdUTC || dataset.createdAt).toLocaleDateString()}${dataset.isReferenceDataset ? ' (Current Reference)' : ''}`
  }));

  return (
    <>
      {/* QuickChat Access Control */}
      <div className="configuration-segment" style={{ marginTop: '30px' }}>
        <h2 style={{ marginBottom: "20px" }}>QuickChat Access Control</h2>
        <div className="config-item" style={{ marginBottom: '30px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '15px 0'
          }}>
            <div>
              <label style={{ 
                display: "block", 
                marginBottom: "8px",
                fontSize: '14px',
                fontWeight: '600'
              }}>
                Enable QuickChat for Users with QuickChat Role
              </label>
              <div style={{ 
                fontSize: '12px', 
                color: '#666',
                fontStyle: 'italic',
                maxWidth: '400px',
                lineHeight: '1.4'
              }}>
                When enabled, QuickChat will be available for users with QuickChat role.<br/>
                When disabled, QuickChat feature will be hidden from all users.
              </div>
            </div>
            <Checkbox
              toggle
              checked={quickChatDisabled}
              onChange={handleQuickChatToggle}
              disabled={toggleLoading}
              style={{ marginLeft: '20px' }}
            />
          </div>
        </div>
      </div>

      <div className="configuration-segment" style={{ 
        marginTop: '30px',
        opacity: !quickChatDisabled ? 0.5 : 1,
        pointerEvents: !quickChatDisabled ? 'none' : 'auto'
      }}>
        <h2 style={{ marginBottom: "20px" }}>Quickchat Reference Dataset</h2>
        
        {/* Current Reference Dataset Display */}
        {currentReferenceDataset && (
          <div
            style={{
              backgroundColor: !quickChatDisabled ? '#f5f5f5' : '#f8f9fa',
              border: `1px solid ${!quickChatDisabled ? '#ccc' : '#28a745'}`,
              borderRadius: '16px',
              padding: '16px 20px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div
                style={{
                  backgroundColor: !quickChatDisabled ? '#999' : '#28a745',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                ✓
              </div>
              <div>
                <div style={{ 
                  fontWeight: '600', 
                  color: !quickChatDisabled ? '#666' : '#2c3e50',
                  fontSize: '14px',
                  marginBottom: '2px'
                }}>
                  Reference Dataset Active
                </div>
                <div style={{ 
                  color: !quickChatDisabled ? '#666' : '#28a745', 
                  fontSize: '16px',
                  fontWeight: '500'
                }}>
                  {datasets.find(d => d.id === currentReferenceDataset.sourceDatasetId)?.datasetName || 'Unknown Dataset'}
                </div>
              </div>
            </div>
            <div style={{ 
              color: '#6c757d', 
              fontSize: '13px',
              fontStyle: 'italic'
            }}>
              Set on {new Date(currentReferenceDataset.createdAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
          </div>
        )}
        {/* Dataset Selection */}
        <div className="config-item">
          <label style={{ 
            display: "block", 
            marginBottom: "8px",
            color: !quickChatDisabled ? '#999' : 'inherit'
          }}>
            Reference Dataset
          </label>
          <Dropdown
            placeholder="Choose a dataset to use as reference for QuickChat"
            fluid
            search
            selection
            clearable
            options={datasetOptions}
            value={selectedDatasetId}
            onChange={(e, { value }) => setSelectedDatasetId(value)}
            disabled={loading || !quickChatDisabled}
          />
          <div style={{ 
            fontSize: '12px', 
            color: !quickChatDisabled ? '#999' : '#666', 
            marginTop: '5px',
            fontStyle: 'italic'
          }}>
            Select a dataset to serve as the configuration template for all QuickChat sessions.
            {!quickChatDisabled && ' (Read-only: QuickChat Access Control is disabled)'}
          </div>
          {datasets.length === 0 && quickChatDisabled && (
            <Message warning style={{ marginTop: "10px" }}>
              <Message.Header>No Datasets Available</Message.Header>
              <p>Please create a dataset first before setting up reference configurations.</p>
            </Message>
          )}
        </div>
        {/* Action Buttons */}
        <div className="button-group" style={{ marginTop: "15px" }}>
          <Button
            className={`save-button ${(!selectedDatasetId || loading || !quickChatDisabled) ? "disabled-button" : ""}`}
            onClick={handleSetReference}
            disabled={!selectedDatasetId || loading || !quickChatDisabled}
            loading={loading}
            style={{ minWidth: "80px" }}
          >
            Save
          </Button>
          
          {currentReferenceDataset && (
            <Button
              className="delete-button"
              onClick={handleClearReference}
              disabled={loading || !quickChatDisabled}
              loading={loading}
              style={{ minWidth: "80px" }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>


      {/* QuickChat Assistant Label Configuration */}
      <div className="configuration-segment" style={{ 
        marginTop: '30px',
        opacity: !quickChatDisabled ? 0.5 : 1,
        pointerEvents: !quickChatDisabled ? 'none' : 'auto'
      }}>
        <h2 style={{ marginBottom: "20px" }}>QuickChat Assistant Label</h2>
        
        <div className="config-item">
          <label style={{ 
            display: "block", 
            marginBottom: "8px",
            color: !quickChatDisabled ? '#999' : 'inherit'
          }}>
            Sidebar Label for QuickChat Assistant
          </label>
          <Input
            type="text"
            value={quickChatLabel}
            onChange={(e) => setQuickChatLabel(e.target.value)}
            placeholder="Enter custom label for QuickChat Assistant"
            fluid
            disabled={labelLoading || !quickChatDisabled}
            maxLength={50}
          />
          <div style={{ 
            fontSize: '12px', 
            color: !quickChatDisabled ? '#999' : '#666', 
            marginTop: '5px',
            fontStyle: 'italic'
          }}>
            This label will appear in the sidebar navigation for the QuickChat Assistant option.
            {!quickChatDisabled && ' (Read-only: QuickChat Access Control is disabled)'}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="button-group" style={{ marginTop: "15px" }}>
          <Button
            className={`save-button ${(!quickChatLabel.trim() || labelLoading || !quickChatDisabled) ? "disabled-button" : ""}`}
            onClick={handleSaveQuickChatLabel}
            disabled={!quickChatLabel.trim() || labelLoading || !quickChatDisabled}
            loading={labelLoading}
            style={{ minWidth: "80px" }}
          >
            Save
          </Button>
          
          <Button
            className="delete-button"
            onClick={handleResetQuickChatLabel}
            disabled={labelLoading || !quickChatDisabled}
            style={{ minWidth: "80px" }}
          >
            Reset
          </Button>
        </div>
      </div>

    </>
  );
};

export default ReferenceDatasetSettings;